// Setup → AI policies (#/setup/ai, #/setup/ai/:siteId; api-phase3.md "AI profile"). Supervisors read and may pause or
// resume the agent; admins edit. Prototype renderSetup 'ai' (global defaults) and siteDetail 'ai' (autonomy levels).
//   global   level 0–4 (Phase 5: every level; each increase still runs 14 days in shadow), target vs live level, shadow
//            period with a countdown to promotion, auto-resolve threshold, device confidence threshold (talk-down and
//            relay pulses below it ask first), authorized-activity policy, QA sample rate, dead-man, budgets.
//            Level names and descriptions come from the API.
//   stages   two-stage triage (2026-10-30): the stage 1 model (behavior assessment), its mode (live, shadow, off) and
//            threshold, and the stage 2 model (the full agent's deeper review), globally and per site. A site's model
//            left empty follows the global one. Globally, stage 2 is the current model.
//   models   the current model (admin), and models admins add to compare: Claude, or another vendor's through
//            OpenRouter. Switching applies to new events; each entry needs its provider cleared and a key configured
//   pause    global and per site, with a reason (supervisors)
//   sites    every site's effective policy; a site override, or "inherit global" (DELETE, 409 if it would raise
//            the live level without shadow)

import { useState } from 'react';
import {
  api,
  BUDGET_KEYS,
  type AgentBudgets,
  type AiPolicy,
  type AiPolicyPatch,
  type AiSetup,
  type AutonomyLevel,
  type AuthorizedMatchPolicy,
  type ModelConfig,
  type NewModelConfig,
  type SiteAiPolicy,
  type Stage1Mode,
} from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { ErrorBox, InlineError, Loading, Pill } from '../../components/ui.tsx';
import { levelLabel, LEVEL_NAMES, pct } from '../../console/ai.ts';
import { useLoad, useNow } from '../../hooks.ts';
import { href } from '../../router.ts';
import { useSession } from '../../session.tsx';
import { fmtDateTime, OPERATOR_TZ } from '../../time.ts';
import { AiRulesEditor } from './AiRulesEditor.tsx';
import { SaveBar } from './common.tsx';

/** Phase 5: every level can be set (each increase still runs 14 days in shadow first). */
const MAX_LEVEL_NOW = 4;

const FALLBACK_LEVELS: AutonomyLevel[] = [
  { level: 0, name: 'Shadow', description: 'Observe and log verdicts. Nothing is acted on.' },
  { level: 1, name: 'Triage', description: 'Resolve clear non-threats; escalate everything else with findings.' },
  { level: 2, name: 'Deter', description: 'Plus talk-down and pulsed deterrents.' },
  { level: 3, name: 'Co-pilot', description: 'Plus customer notifications and operator instructions.' },
  { level: 4, name: 'Resolve', description: 'Plus closing clear-cut events end to end.' },
];

const BUDGET_META: Record<keyof AgentBudgets, { label: string; unit: string; step: number }> = {
  max_tool_calls: { label: 'Tool calls', unit: 'calls', step: 1 },
  max_model_calls: { label: 'Model calls', unit: 'calls', step: 1 },
  max_wall_s: { label: 'Working time', unit: 's', step: 5 },
  max_cost: { label: 'Cost', unit: 'USD', step: 0.05 },
  max_frames: { label: 'Frames looked at', unit: 'frames', step: 1 },
};

function left(msLeft: number): string {
  if (msLeft <= 0) return 'now';
  const m = Math.floor(msLeft / 60_000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${mm}m` : `${mm}m`;
}

/** "Level 1 goes live on Oct 30 14:05 MST · in 13d 22h". */
/** Shown after an admin made a level live before its shadow period ended. */
function EarlyLive({ p }: { p: AiPolicy }) {
  const e = p.earlyLive;
  if (!e || p.shadowLevel !== null) return null;
  return (
    <div className="note warn-t">
      <Icon name="alert" /> Level {p.liveLevel} went live early on {fmtDateTime(e.at, OPERATOR_TZ)}, after {e.shadowDays} of 14 shadow days, by {e.byName ?? e.by ?? 'an admin'}: “{e.reason}”
    </div>
  );
}

/** Admin override of the shadow period: make the level in shadow live now, with a reason. */
function GoLiveNow({ p, onGoLive }: { p: AiPolicy; onGoLive: (level: number, reason: string) => Promise<AiPolicy> }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (p.shadowLevel === null) return null;
  const level = p.shadowLevel;
  const ok = reason.trim().length >= 20 && reason.trim().length <= 500;
  if (!open)
    return (
      <button type="button" className="btn sm" onClick={() => setOpen(true)}>
        <Icon name="alert" />
        Make Level {level} live now…
      </button>
    );
  return (
    <div className="confirmbar col" role="group" aria-label={`Make Level ${level} live now`}>
      <span>
        <b>Skip the rest of the shadow period?</b> Level {level} starts acting on new events at once, without the remaining shadow days that measure what it would do. This is recorded in the
        audit log with your reason, and supervisors get a notice to review it.
      </span>
      <textarea aria-label="Reason" placeholder="Why this level should act now (at least 20 characters), e.g. what was reviewed instead of the shadow period" value={reason} maxLength={500} rows={3} onChange={(e) => setReason(e.target.value)} />
      <span className="row-wrap">
        <button
          type="button"
          className="btn pri"
          disabled={!ok || busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onGoLive(level, reason.trim());
              setOpen(false);
              setReason('');
            } catch (e) {
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Working…' : `Make Level ${level} live now`}
        </button>
        <button type="button" className="btn" onClick={() => (setOpen(false), setError(null))} disabled={busy}>
          Cancel
        </button>
        <span className="note">{reason.trim().length}/20 characters minimum</span>
      </span>
      <InlineError error={error} />
    </div>
  );
}

function Promotion({ p }: { p: AiPolicy }) {
  const now = useNow(30_000);
  if (p.shadowLevel === null || !p.shadowUntil) return null;
  const t = Date.parse(p.shadowUntil);
  return (
    <div className="promo" role="status">
      <Icon name="log" />
      <span>
        <b>{levelLabel(p.shadowLevel)}</b> is running in shadow. <b>Level {p.shadowLevel} goes live on {fmtDateTime(p.shadowUntil, OPERATOR_TZ)}</b>
        {Number.isFinite(t) && (
          <>
            {' '}
            · in <b className="mono">{left(t - now)}</b>
          </>
        )}
        . Until then the live level stays at {p.liveLevel}.
      </span>
    </div>
  );
}

function LevelPicker({ levels, value, live, shadow, onChange, disabled }: { levels: AutonomyLevel[]; value: number; live: number; shadow: number | null; onChange: (n: number) => void; disabled: boolean }) {
  const list = levels.length ? levels : FALLBACK_LEVELS;
  return (
    <div className="lvls" role="radiogroup" aria-label="Autonomy level">
      {list.map((l) => {
        const later = l.level > MAX_LEVEL_NOW;
        return (
          <button
            key={l.level}
            type="button"
            role="radio"
            aria-checked={value === l.level}
            className={`lvl${value === l.level ? ' on' : ''}${live === l.level ? ' live' : shadow === l.level ? ' shadowing' : ''}`}
            onClick={() => onChange(l.level)}
            disabled={disabled || later}
            title={later ? 'Not available on this server yet' : l.description}
          >
            <span className="mono dim">Level {l.level}</span>
            <b>{l.name || LEVEL_NAMES[l.level]}</b>
            <small>{l.description}</small>
          </button>
        );
      })}
    </div>
  );
}

type Draft = {
  level: number;
  autoResolveThreshold: number;
  deviceConfidenceThreshold: number;
  authorizedMatchPolicy: AuthorizedMatchPolicy;
  qaSampleRate: number;
  deadManS: number;
  budgets: AgentBudgets;
  stage1Mode: Stage1Mode;
  stage1MinConfidence: number;
  /** '' follows the global model (sites only). */
  stage1Model: string;
  stage2Model: string;
};
/** The stage 2 model a draft starts from: the site's own ('' follows global), or globally the current model. */
const stage2Of = (p: AiPolicy, currentId: string | null) => (p.scope === 'global' ? (currentId ?? '') : (p.stage2?.modelConfigId ?? ''));
const draftOf = (p: AiPolicy, currentId: string | null): Draft => ({
  level: p.level,
  autoResolveThreshold: p.autoResolveThreshold,
  deviceConfidenceThreshold: p.deviceConfidenceThreshold ?? 0.85,
  authorizedMatchPolicy: p.authorizedMatchPolicy,
  qaSampleRate: p.qaSampleRate,
  deadManS: p.deadManS,
  budgets: { ...p.budgets },
  stage1Mode: p.stage1?.mode ?? 'live',
  stage1MinConfidence: p.stage1?.minConfidence ?? 0.85,
  stage1Model: p.scope === 'inherited' ? '' : (p.stage1?.modelConfigId ?? ''),
  stage2Model: stage2Of(p, currentId),
});

function patchOf(d: Draft, p: AiPolicy, currentId: string | null): AiPolicyPatch {
  const out: AiPolicyPatch = {};
  if (d.level !== p.level) out.level = d.level;
  if (d.autoResolveThreshold !== p.autoResolveThreshold) out.autoResolveThreshold = d.autoResolveThreshold;
  if (d.deviceConfidenceThreshold !== (p.deviceConfidenceThreshold ?? 0.85)) out.deviceConfidenceThreshold = d.deviceConfidenceThreshold;
  if (d.authorizedMatchPolicy !== p.authorizedMatchPolicy) out.authorizedMatchPolicy = d.authorizedMatchPolicy;
  if (d.qaSampleRate !== p.qaSampleRate) out.qaSampleRate = d.qaSampleRate;
  if (d.deadManS !== p.deadManS) out.deadManS = d.deadManS;
  const b: Partial<AgentBudgets> = {};
  for (const k of BUDGET_KEYS) if (d.budgets[k] !== p.budgets[k]) b[k] = d.budgets[k];
  if (Object.keys(b).length) out.budgets = b;
  const s1: NonNullable<AiPolicyPatch['stage1']> = {};
  if (d.stage1Mode !== (p.stage1?.mode ?? 'live')) s1.mode = d.stage1Mode;
  if (d.stage1MinConfidence !== (p.stage1?.minConfidence ?? 0.85)) s1.minConfidence = d.stage1MinConfidence;
  if (d.stage1Model !== (p.scope === 'inherited' ? '' : (p.stage1?.modelConfigId ?? ''))) s1.modelConfigId = d.stage1Model || null;
  if (Object.keys(s1).length) out.stage1 = s1;
  if (d.stage2Model !== stage2Of(p, currentId)) out.stage2 = { modelConfigId: d.stage2Model || null };
  return out;
}

const modelLabel = (m: ModelConfig) => `${m.name}${m.provider === 'openrouter' ? ' (via OpenRouter)' : ''}${m.effort ? ` · ${m.effort}` : ''}`;

/** Two-stage triage: which model looks first, what it may do, and which model reviews what it sends on. */
function Stages({ d, set, models, site, what, globalStage1, currentId }: { d: Draft; set: <K extends keyof Draft>(k: K, v: Draft[K]) => void; models: ModelConfig[]; site: boolean; what: string; globalStage1: string | null; currentId: string | null }) {
  const name = (id: string | null) => models.find((m) => m.id === id)?.name ?? 'none';
  const chosen = (id: string, fallback: string | null) => models.find((m) => m.id === (id || fallback));
  const s1 = chosen(d.stage1Model, globalStage1);
  const s2 = chosen(d.stage2Model, currentId);
  const external = [s1, s2].some((m) => m?.provider === 'openrouter');
  return (
    <div className="sec">
      <div className="lbl">
        <span>Two-stage AI triage</span>
        <span className="dim">stage 1 looks at every new alarm; stage 2 reviews what it sends on</span>
      </div>
      <div className="trow">
        <label htmlFor={`s1m-${what}`}>
          <b>Stage 1 · behavior assessment.</b> Evaluates the event's clips and frames and returns a structured assessment: subjects, behaviors, risk, verdict
        </label>
        <span className="sp" />
        <span className="ctl">
          <select id={`s1m-${what}`} className="compact" value={d.stage1Model} onChange={(e) => set('stage1Model', e.target.value)}>
            {site && <option value="">Global ({name(globalStage1)})</option>}
            {models
              .filter((m) => m.provider !== 'simulator')
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {modelLabel(m)}
                </option>
              ))}
          </select>
        </span>
      </div>
      <div className="trow">
        <label htmlFor={`s1o-${what}`}>What stage 1 may do</label>
        <span className="sp" />
        <span className="ctl">
          <select id={`s1o-${what}`} className="compact" value={d.stage1Mode} onChange={(e) => set('stage1Mode', e.target.value as Stage1Mode)}>
            <option value="live">Live: close routine events itself (within the autonomy level)</option>
            <option value="shadow">Shadow: record its assessment only</option>
            <option value="off">Off: stage 2 looks at every event</option>
          </select>
        </span>
      </div>
      <div className="trow">
        <label htmlFor={`s1c-${what}`}>Stage 1 keeps a routine, low-risk Clear or Authorized event at or above</label>
        <span className="sp" />
        <span className="ctl">
          <input id={`s1c-${what}`} type="range" min={50} max={100} step={1} value={Math.round(d.stage1MinConfidence * 100)} onChange={(e) => set('stage1MinConfidence', Number(e.target.value) / 100)} />
          <b className="mono">{pct(d.stage1MinConfidence)}</b>
        </span>
      </div>
      <div className="note">
        Anything below that, medium or high risk, a high-risk behavior (forcing, climbing, trying doors, carrying items away, vandalism, fire, a person down, a weapon), a VMS-verified alarm, or a
        person seen earlier in the event goes to stage 2. Closing still needs the auto-resolve threshold and a level that allows it; below Level 1 it is recorded as a would-have.
      </div>
      <div className="trow">
        <label htmlFor={`s2m-${what}`}>
          <b>Stage 2 · contextual review.</b> The full agent with tools, for ambiguous or high-risk events: deeper review and threat assessment
        </label>
        <span className="sp" />
        <span className="ctl">
          <select id={`s2m-${what}`} className="compact" value={d.stage2Model} onChange={(e) => set('stage2Model', e.target.value)}>
            {site && <option value="">Global ({name(currentId)})</option>}
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {modelLabel(m)}
              </option>
            ))}
          </select>
        </span>
      </div>
      {!site && <div className="note">The global stage 2 model is the current model in the list below.</div>}
      {external && <div className="note warn-t">An OpenRouter model sends customer camera frames to OpenRouter and the model's host. Use it on customer events only after its vendor review.</div>}
      <div className="note">A model change applies to events that open after it; open events keep the models they started with.</div>
    </div>
  );
}

/** The policy form, for the global row or one site. */
function PolicyForm({
  policy,
  levels,
  models,
  globalStage1,
  canEdit,
  onSave,
  onGoLive,
  what,
}: {
  policy: AiPolicy;
  levels: AutonomyLevel[];
  models: ModelConfig[];
  /** The global stage 1 model, which a site without its own follows. */
  globalStage1: string | null;
  canEdit: boolean;
  onSave: (patch: AiPolicyPatch) => Promise<AiPolicy>;
  /** Admin override of the shadow period (admins only). */
  onGoLive?: (level: number, reason: string) => Promise<AiPolicy>;
  what: string;
}) {
  const currentId = models.find((m) => m.current)?.id ?? null;
  const [d, setD] = useState<Draft>(() => draftOf(policy, currentId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const patch = patchOf(d, policy, currentId);
  const dirty = Object.keys(patch).length > 0;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setSaved(false);
    setD((o) => ({ ...o, [k]: v }));
  };
  const raising = d.level > policy.liveLevel && d.level !== policy.level;
  const lowering = d.level < policy.liveLevel;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // A global stage 2 change makes that model current (the parent saves it), so the draft compares against it.
      const next = await onSave(patch);
      setD(draftOf(next, patch.stage2 && next.scope === 'global' ? patch.stage2.modelConfigId : currentId));
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const numBad = !(d.deadManS >= 10 && d.deadManS <= 60) || !(d.qaSampleRate >= 0 && d.qaSampleRate <= 1) || BUDGET_KEYS.some((k) => !(d.budgets[k] > 0));
  return (
    <fieldset disabled={!canEdit} className="tabbody aiset">
      <div className="sec">
        <div className="lbl">
          <span>Agent autonomy · {what}</span>
          <span className="dim">
            Live <b className="fg">{levelLabel(policy.liveLevel)}</b> · target {levelLabel(policy.level)}
          </span>
        </div>
        <LevelPicker levels={levels} value={d.level} live={policy.liveLevel} shadow={policy.shadowLevel} onChange={(n) => set('level', n)} disabled={!canEdit} />
        <Promotion p={policy} />
        {canEdit && onGoLive && <GoLiveNow p={policy} onGoLive={onGoLive} />}
        <EarlyLive p={policy} />
        {raising && <div className="note warn-t">Raising to Level {d.level} runs it in shadow for 14 days first. The live level stays at {policy.liveLevel} until then.</div>}
        {lowering && <div className="note warn-t">Lowering to Level {d.level} acts at once.</div>}
        <div className="note">A new level runs in shadow for 14 days first: the agent records what it would have done (“would have auto-resolved at 02:13”) before it acts on it.</div>
      </div>

      <Stages d={d} set={set} models={models} site={policy.scope !== 'global'} what={what} globalStage1={globalStage1} currentId={currentId} />

      <div className="sec">
        <div className="lbl">Thresholds and checks</div>
        <div className="trow">
          <label htmlFor={`thr-${what}`}>Auto-resolve when the AI says Clear or Authorized at or above</label>
          <span className="sp" />
          <span className="ctl">
            <input id={`thr-${what}`} type="range" min={50} max={100} step={1} value={Math.round(d.autoResolveThreshold * 100)} onChange={(e) => set('autoResolveThreshold', Number(e.target.value) / 100)} />
            <b className="mono">{pct(d.autoResolveThreshold)}</b>
          </span>
        </div>
        <div className="trow">
          <label htmlFor={`dev-${what}`}>Talk-down and deterrent pulses on its own (Level 2+) when its confidence is at or above</label>
          <span className="sp" />
          <span className="ctl">
            <input id={`dev-${what}`} type="range" min={50} max={100} step={1} value={Math.round(d.deviceConfidenceThreshold * 100)} onChange={(e) => set('deviceConfidenceThreshold', Number(e.target.value) / 100)} />
            <b className="mono">{pct(d.deviceConfidenceThreshold)}</b>
          </span>
        </div>
        <div className="note">Below it, while an operator has the event, or when authorized activity may be on site, the agent asks first (20 s, Y / N).</div>
        <div className="trow">
          <label htmlFor={`amp-${what}`}>When activity matches authorized activity</label>
          <span className="sp" />
          <span className="ctl">
            <select id={`amp-${what}`} className="compact" value={d.authorizedMatchPolicy} onChange={(e) => set('authorizedMatchPolicy', e.target.value as AuthorizedMatchPolicy)}>
              <option value="resolve">Auto-resolve it (Level 1+)</option>
              <option value="escalate_low">Escalate at low priority</option>
            </select>
          </span>
        </div>
        <div className="trow">
          <label htmlFor={`qa-${what}`}>QA sample of AI-resolved events (blind supervisor review)</label>
          <span className="sp" />
          <span className="ctl">
            <input id={`qa-${what}`} type="number" min={0} max={100} step={0.5} value={Math.round(d.qaSampleRate * 1000) / 10} onChange={(e) => set('qaSampleRate', Number(e.target.value) / 100)} />
            <span className="muted">%</span>
          </span>
        </div>
        <div className="trow">
          <label htmlFor={`dm-${what}`}>Dead-man: escalate to a human if the agent has not finished within</label>
          <span className="sp" />
          <span className="ctl">
            <input id={`dm-${what}`} type="number" min={10} max={60} step={1} value={d.deadManS} onChange={(e) => set('deadManS', Number(e.target.value))} />
            <span className="muted">s (10–60)</span>
          </span>
        </div>
      </div>

      <div className="sec">
        <div className="lbl">
          <span>Budget per event</span>
          <span className="dim">past a budget the agent escalates; escalate itself is never blocked</span>
        </div>
        {BUDGET_KEYS.map((k) => (
          <div key={k} className="trow">
            <label htmlFor={`b-${k}-${what}`}>{BUDGET_META[k].label}</label>
            <span className="sp" />
            <span className="ctl">
              <input
                id={`b-${k}-${what}`}
                type="number"
                min={BUDGET_META[k].step}
                step={BUDGET_META[k].step}
                value={d.budgets[k]}
                onChange={(e) => set('budgets', { ...d.budgets, [k]: Number(e.target.value) })}
              />
              <span className="muted">{BUDGET_META[k].unit}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="sec">
        <div className="lbl">Guardrails</div>
        <div className="trow">
          <span>Always send to a human: panic, duress, fire</span>
          <span className="sp" />
          <span className="pill">
            <Icon name="lock" />
            Always on
          </span>
        </div>
        <div className="trow">
          <span>AI may open gates or locks, or hold a relay on (it may only pulse strobes, sirens and lights)</span>
          <span className="sp" />
          <span className="pill">
            <Icon name="lock" />
            Never
          </span>
        </div>
        <div className="trow">
          <span>AI may dispatch police, call 911, unlock gates or disarm</span>
          <span className="sp" />
          <span className="pill">
            <Icon name="lock" />
            Never
          </span>
        </div>
        <div className="note">The policy is enforced server-side in the tool API, not in the prompt.</div>
      </div>

      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={busy}
        error={error}
        saved={saved}
        onSave={() => void save()}
        onReset={() => {
          setD(draftOf(policy, currentId));
          setError(null);
        }}
        extra={numBad ? <span className="note bad-t">Check the numbers: dead-man 10–60 s, QA 0–100 %, budgets above 0.</span> : undefined}
      />
    </fieldset>
  );
}

/** Pause / resume the agent globally or for one site, with a reason (supervisors and admins). */
function PauseCard({ scope, siteId, policy, canPause, onChanged }: { scope: 'global' | 'site'; siteId?: string; policy: AiPolicy; canPause: boolean; onChanged: (p: AiPolicy) => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const paused = scope === 'global' ? policy.globalPaused : policy.paused && !policy.globalPaused;
  const go = async (p: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.setAgentPaused({ scope, siteId, paused: p, reason: p ? reason.trim() || undefined : undefined });
      onChanged(r);
      setAsking(false);
      setReason('');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const what = scope === 'global' ? 'all sites' : 'this site';
  return (
    <div className="sec">
      {paused ? (
        <div className="pausebar" role="status">
          <Icon name="pause" />
          <span>
            <b>The AI agent is paused for {what}.</b> Every event goes to an operator; open runs stop acting.
            {policy.pausedByName ? ` Paused by ${policy.pausedByName}.` : ''}
            {policy.pauseReason ? ` Reason: “${policy.pauseReason}”.` : ''}
          </span>
          <span className="sp" />
          {canPause && (
            <button type="button" className="btn pri" onClick={() => void go(false)} disabled={busy}>
              <Icon name="play" />
              {busy ? 'Resuming…' : 'Resume the agent'}
            </button>
          )}
        </div>
      ) : scope === 'site' && policy.globalPaused ? (
        <div className="note warn-t">Paused for all sites (global). Resume it above.</div>
      ) : asking ? (
        <div className="confirmbar" role="group" aria-label="Pause the agent">
          <span>
            Pause the AI agent for <b>{what}</b>? Events go straight to operators until it is resumed.
          </span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" aria-label="Reason" autoFocus onKeyDown={(e) => e.key === 'Enter' && void go(true)} />
          <button type="button" className="btn pri" onClick={() => void go(true)} disabled={busy}>
            {busy ? 'Pausing…' : 'Pause'}
          </button>
          <button type="button" className="btn" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="trow">
          <span>
            Agent is <b className="ok-t">running</b> for {what}.
          </span>
          <span className="sp" />
          {canPause ? (
            <button type="button" className="btn" onClick={() => setAsking(true)}>
              <Icon name="pause" />
              Pause for {what}
            </button>
          ) : (
            <span className="note">Supervisors can pause it.</span>
          )}
        </div>
      )}
      <InlineError error={error} />
    </div>
  );
}

const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];

/** Add a model to compare: a Claude model ID, or an OpenRouter one (vendor/model, as listed on openrouter.ai/models). */
function AddModel({ onAdded }: { onAdded: (m: ModelConfig[]) => void }) {
  const blank: NewModelConfig = { name: '', provider: 'openrouter', model: '', effort: 'medium' };
  const [d, setD] = useState<NewModelConfig>(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = <K extends keyof NewModelConfig>(k: K, v: NewModelConfig[K]) => setD((o) => ({ ...o, [k]: v }));
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      onAdded(await api.addAiModel({ ...d, name: d.name.trim(), model: d.model.trim() }));
      setD(blank);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="sec">
      <div className="lbl">Add a model to compare</div>
      <div className="addmodel">
        <select className="compact" aria-label="Provider" value={d.provider} onChange={(e) => set('provider', e.target.value as NewModelConfig['provider'])}>
          <option value="openrouter">OpenRouter</option>
          <option value="anthropic">Anthropic</option>
        </select>
        <input className="compact mono" aria-label="Model ID" placeholder={d.provider === 'anthropic' ? 'claude-sonnet-5-5' : 'vendor/model'} value={d.model} onChange={(e) => set('model', e.target.value)} />
        <input className="compact" aria-label="Name" placeholder="Name shown here" value={d.name} onChange={(e) => set('name', e.target.value)} />
        <select className="compact" aria-label="Effort" value={d.effort} onChange={(e) => set('effort', e.target.value)}>
          {EFFORTS.map((x) => (
            <option key={x} value={x}>
              effort {x}
            </option>
          ))}
        </select>
        <button type="button" className="btn" disabled={busy || !d.name.trim() || !d.model.trim()} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
      <small className="note">Added models aren't used until you pick one above. Switching applies to new events; open events keep their model.</small>
      <InlineError error={error} />
    </div>
  );
}

function Models({ models, stage1Id, canEdit, onChanged }: { models: ModelConfig[]; stage1Id: string | null; canEdit: boolean; onChanged: (m: ModelConfig[]) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const pick = async (m: ModelConfig) => {
    if (m.current) return;
    setBusy(m.id);
    setError(null);
    try {
      onChanged(await api.putAiModel(m.id));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card2">
      <div className="card-h">
        <h3>
          <Icon name="spark" />
          Models
        </h3>
        <span className="card-r note">{canEdit ? 'Use this: makes a model the global stage 2 model. Stage 1 is chosen above.' : 'Admins choose the models.'}</span>
      </div>
      <div className="models" role="radiogroup" aria-label="Model">
        {models.map((m) => {
          const claude = m.provider === 'anthropic';
          const router = m.provider === 'openrouter';
          return (
            <button key={m.id} type="button" role="radio" aria-checked={m.current} className={`model${m.current ? ' on' : ''}`} disabled={!canEdit || !!busy} onClick={() => void pick(m)}>
              <span className="rd" aria-hidden="true" />
              <span>
                <b>{claude ? `${m.name} (Anthropic Claude)` : router ? `${m.name} (via OpenRouter)` : m.name}</b>
                <small>
                  <span className="mono">{m.model}</span>
                  {m.effort ? ` · effort ${m.effort}` : ''}
                  {m.promptVersion ? ` · prompt ${m.promptVersion}` : ''}
                </small>
                {claude && (
                  <small className="warn-t">
                    Sends customer camera frames to Anthropic. Real calls need the provider cleared for customer frames and ANTHROPIC_API_KEY configured on the agent runtime.
                  </small>
                )}
                {router && (
                  <small className="warn-t">
                    Sends customer camera frames to OpenRouter and the model's host, on zero-data-retention endpoints only. Real calls need the vendor cleared for customer frames and OPENROUTER_API_KEY configured on the agent runtime.
                  </small>
                )}
                {m.provider === 'simulator' && <small>Rules-based, runs locally. No frames leave the SOC.</small>}
              </span>
              <span className="row-wrap">
                {m.id === stage1Id && <Pill>stage 1</Pill>}
                {m.current ? <Pill tone="ai">current · stage 2</Pill> : busy === m.id ? <span className="note">Switching…</span> : <span className="note">{canEdit ? 'Use this' : ''}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <InlineError error={error} />
      {canEdit && <AddModel onAdded={onChanged} />}
    </section>
  );
}

/* ---------- one site ---------- */

/** One site's AI policy: used in Setup → AI and in the site's "AI agent" tab. */
export function SiteAiPanel({
  siteId,
  siteName,
  canEdit,
  canPause,
  setup,
  onChanged,
}: {
  siteId: string;
  siteName?: string;
  canEdit: boolean;
  canPause: boolean;
  /** Levels, models and the global policy, when the caller has them. */
  setup?: Pick<AiSetup, 'levels' | 'models' | 'global'>;
  onChanged?: () => void;
}) {
  const load = useLoad((sig) => api.siteAi(siteId, sig), [siteId]);
  const lv = useLoad((sig) => (setup ? Promise.resolve(setup) : api.aiSetup(sig)), [siteId, setup]);
  const [inherit, setInherit] = useState<'ask' | 'busy' | null>(null);
  const [inhErr, setInhErr] = useState<unknown>(null);
  const p = load.data;
  if (load.error && !p) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the site's AI policy" />;
  if (!p) return <Loading label="Loading AI policy…" />;
  const after = (n: AiPolicy) => {
    load.setData((o) => ({ ...(o ?? (n as SiteAiPolicy)), ...n }));
    onChanged?.();
  };
  const doInherit = async () => {
    setInherit('busy');
    setInhErr(null);
    try {
      after(await api.inheritSiteAi(siteId));
      setInherit(null);
    } catch (e) {
      setInhErr(e);
      setInherit('ask');
    }
  };
  const own = p.scope === 'site';
  return (
    <div className="tabbody">
      <div className="row-wrap">
        {own ? <Pill tone="ai">Site override</Pill> : <Pill>Inherits the global policy</Pill>}
        <span className="note">
          {own ? 'Changes here apply to this site only.' : 'Saving any change creates a site override from the global policy.'}
          {p.version ? ` · version ${p.version}` : ''}
        </span>
        <span className="sp" />
        {own && canEdit && inherit === null && (
          <button type="button" className="btn" onClick={() => setInherit('ask')}>
            <Icon name="sync" />
            Inherit global
          </button>
        )}
      </div>
      {inherit && (
        <div className="confirmbar" role="group" aria-label="Inherit global">
          <span>
            Drop {siteName ? <b>{siteName}</b> : 'this site'}'s override and use the global policy? Refused if that would raise its live level without a shadow period.
          </span>
          <button type="button" className="btn pri" onClick={() => void doInherit()} disabled={inherit === 'busy'}>
            {inherit === 'busy' ? 'Working…' : 'Inherit global'}
          </button>
          <button type="button" className="btn" onClick={() => (setInherit(null), setInhErr(null))}>
            Cancel
          </button>
        </div>
      )}
      <InlineError error={inhErr} />
      {p.humanReviewOnly && (
        <div className="banner warnish">
          <Icon name="users" />
          <span>
            <b>Human review only</b> is on for this site (Operations): every event goes to an operator at once. The agent still assesses it.
          </span>
          <a className="btn sm" href={href.setup('sites', siteId, 'ops')}>
            Operations
          </a>
        </div>
      )}
      <PauseCard scope="site" siteId={siteId} policy={p} canPause={canPause} onChanged={after} />
      <PolicyForm
        key={`${p.scope}-${lv.data ? 'm' : '-'}`}
        policy={p}
        levels={lv.data?.levels ?? FALLBACK_LEVELS}
        models={lv.data?.models ?? []}
        globalStage1={lv.data?.global.stage1?.modelConfigId ?? null}
        canEdit={canEdit}
        onGoLive={async (level, reason) => {
          const n = await api.goLiveNow(siteId, level, reason);
          after(n);
          return n;
        }}
        what={siteName ?? 'this site'}
        onSave={async (patch) => {
          const n = await api.putSiteAi(siteId, patch);
          after(n);
          return n;
        }}
      />
    </div>
  );
}

/* ---------- section ---------- */

function SitesTable({ setup, selected }: { setup: AiSetup; selected: string | null }) {
  const sites = useLoad((sig) => api.sites(undefined, sig), []);
  const [q, setQ] = useState('');
  const own = new Map(setup.sites.map((x) => [x.siteId, x]));
  const all = (sites.data ?? []).filter((x) => x.active);
  const needle = q.trim().toLowerCase();
  const rows = all
    .filter((x) => !needle || `${x.name} ${x.companyName}`.toLowerCase().includes(needle))
    .map((x) => ({ id: x.id, name: x.name, company: x.companyName, policy: own.get(x.id)?.policy ?? null }))
    .sort((a, b) => Number(!!b.policy) - Number(!!a.policy) || a.name.localeCompare(b.name));
  // Overrides for sites the master list does not have (inactive) still show.
  for (const o of setup.sites) if (!all.some((x) => x.id === o.siteId)) rows.unshift({ id: o.siteId, name: o.siteName, company: '', policy: o.policy });
  const g = setup.global;
  return (
    <section className="card2 flush">
      <div className="card-h pad">
        <h3>
          <Icon name="map" />
          Sites
        </h3>
        <span className="card-r">
          <input type="search" className="compact" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sites" aria-label="Search sites" />
        </span>
      </div>
      {sites.error && !sites.data ? (
        <ErrorBox error={sites.error} onRetry={sites.reload} title="Could not load sites" />
      ) : !sites.data ? (
        <Loading label="Loading sites…" />
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Site</th>
                <th scope="col">Policy</th>
                <th scope="col">Live level</th>
                <th scope="col">Shadow</th>
                <th scope="col">Auto-resolve</th>
                <th scope="col">QA</th>
                <th scope="col">Agent</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const p = r.policy ?? g;
                return (
                  <tr key={r.id} className={selected === r.id ? 'sel' : ''}>
                    <td>
                      <a href={href.setup('ai', r.id)} className="strong">
                        {r.name}
                      </a>
                      {r.company && <div className="note">{r.company}</div>}
                    </td>
                    <td>{r.policy ? <Pill tone="ai">override</Pill> : <span className="muted">global</span>}</td>
                    <td className="nowrap">{levelLabel(p.liveLevel)}</td>
                    <td className="nowrap">{p.shadowLevel !== null && p.shadowUntil ? <span className="warn-t">L{p.shadowLevel} until {fmtDateTime(p.shadowUntil, OPERATOR_TZ, false).slice(0, 6)}</span> : <span className="dim">—</span>}</td>
                    <td className="mono">{pct(p.autoResolveThreshold)}</td>
                    <td className="mono">{pct(p.qaSampleRate, 1)}</td>
                    <td>{p.paused ? <Pill tone="warn">paused</Pill> : <span className="ok-t small">running</span>}</td>
                    <td>
                      <a className="btn sm" href={href.setup('ai', r.id)}>
                        Open
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function AiEditor({ siteId }: { siteId: string | null }) {
  const s = useSession();
  const canEdit = s.isAdmin;
  const canPause = s.has('supervisor', 'admin');
  const load = useLoad((sig) => api.aiSetup(sig), []);
  // The site's name: from its override row, else the master data (an inherited site has no row).
  const siteRow = useLoad((sig) => (siteId ? api.site(siteId, sig) : Promise.resolve(null)), [siteId]);
  const d = load.data;
  if (load.error && !d) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the AI setup" />;
  if (!d) return <Loading label="Loading AI setup…" />;
  const setGlobal = (g: AiPolicy) => load.setData((o) => (o ? { ...o, global: g } : o));
  const site = siteId ? (d.sites.find((x) => x.siteId === siteId)?.siteName ?? siteRow.data?.name ?? null) : null;
  return (
    <>
      <div className="page-h">
        <div>
          <h2>
            <Icon name="spark" className="ai-t" />
            AI policies
          </h2>
          <p>
            Autonomy, thresholds and budgets for the AI agent. {canEdit ? 'You can edit them.' : 'Supervisors read them and can pause the agent; admins edit.'}
          </p>
        </div>
        <span className="sp" />
        <Pill tone={d.enabled ? 'ok' : 'warn'} title="cfg.setting agent.enabled: the agent runtime is deployed">
          {d.enabled ? 'Agent runtime on' : 'Agent runtime off: every event goes to a human'}
        </Pill>
      </div>

      {siteId ? (
        <section className="card2">
          <div className="card-h">
            <h3>
              <Icon name="map" />
              {site ?? siteId}
            </h3>
            <span className="card-r">
              <a className="btn sm" href={href.setup('ai')}>
                <Icon name="back" />
                All sites
              </a>
              <a className="btn sm" href={href.setup('sites', siteId)}>
                Site setup
              </a>
            </span>
          </div>
          <SiteAiPanel key={siteId} siteId={siteId} siteName={site ?? undefined} canEdit={canEdit} canPause={canPause} setup={d} onChanged={load.reload} />
          <AiRulesEditor key={siteId} siteId={siteId} />
        </section>
      ) : (
        <>
          <section className="card2">
            <div className="card-h">
              <h3>
                <Icon name="spark" />
                Global defaults
              </h3>
              <span className="card-r note">Sites inherit these until they get their own policy · updated version {d.global.version}</span>
            </div>
            <PauseCard scope="global" policy={d.global} canPause={canPause} onChanged={setGlobal} />
            <PolicyForm
              key={d.models.find((m) => m.current)?.id ?? '-'}
              policy={d.global}
              levels={d.levels}
              models={d.models}
              globalStage1={d.global.stage1?.modelConfigId ?? null}
              canEdit={canEdit}
              onGoLive={async (level, reason) => {
                const n = await api.goLiveNow(null, level, reason);
                setGlobal(n);
                return n;
              }}
              what="global"
              onSave={async ({ stage2, ...patch }) => {
                // Globally, stage 2 is the current model.
                if (stage2?.modelConfigId) {
                  const models = await api.putAiModel(stage2.modelConfigId);
                  load.setData((o) => (o ? { ...o, models } : o));
                }
                const n = Object.keys(patch).length ? await api.putAiGlobal(patch) : d.global;
                setGlobal(n);
                return n;
              }}
            />
          </section>
          <AiRulesEditor siteId={null} />
          <Models models={d.models} stage1Id={d.global.stage1?.modelConfigId ?? null} canEdit={canEdit} onChanged={(m) => load.setData((o) => (o ? { ...o, models: m } : o))} />
          <SitesTable setup={d} selected={null} />
        </>
      )}
    </>
  );
}

